import { Server } from "@modules/api";
import TranslatedText from "@components/items/display/TranslatedText";
import { ListeningIcon } from "@components/icons/ListeningIcon";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { useIsLoadingVisibile } from "@utils/hooks/useIsLoadingVisible";
import { Skeleton } from "@ui/skeleton";
import MarkdownView from "react-showdown";
import { OnItemPress } from "@modules/content/hooks/useComponentData";

export interface RepeatedPrayerListParameters {
  title: string | null | undefined;
}

export interface RepeatedPrayerListItemParameters {
  repetition: string | null | undefined;
  source: string | null | undefined;
  text: string | null | undefined;
  title: string | null | undefined;
}

type ItemParams = {
  id: string | undefined;
} & {
  [K in keyof RepeatedPrayerListItemParameters]?:
    | RepeatedPrayerListItemParameters[K]
    | undefined;
};

interface RepeatedPrayerListProps {
  contents: Server.ContentEntityOutput[] | undefined;
  isLoading: boolean;
  parameterValues: RepeatedPrayerListParameters;
  items: ItemParams[];
}

export function RepeatedPrayerList({
  contents,
  items,
  parameterValues,
  isLoading,
}: RepeatedPrayerListProps) {
  const navigate = useAppNavigate();

  const isLoadingVisible = useIsLoadingVisibile(
    isLoading && (!contents || Boolean(contents && !contents.length))
  );

  return (
    <div className={`relative mx-auto w-[830px]`}>
      <div
        className={`w-full ${
          (isLoadingVisible || isLoading) &&
          "z-10 h-[800px] bg-white dark:bg-black"
        } absolute top-0 px-10 ltr:left-0 rtl:right-0`}
      >
        <RepeatedPrayerListLoading isLoading={isLoadingVisible} />
      </div>
      {contents && contents.length ? (
        <div className="px-10 pb-[35px]">
          <div className="mt-[45px] flex w-full flex-col items-center justify-center gap-3">
            {items.map((t, i) => {
              return (
                <div
                  key={t.id}
                  className="relative flex w-full items-center justify-start gap-2 rounded-[10px] border border-[#D9D9D9] bg-[#FAF3E0] bg-cover px-4 pb-[80px] pe-[76px] ps-[33px] pt-[22px] text-[19px] text-ramadan-primary"
                  style={{
                    backgroundImage: "url(/assets/images/prayer-bg.svg)",
                  }}
                >
                  <div className="opacity-70">
                    <MarkdownView
                      markdown={t.text || ""}
                      options={{ tables: true, emoji: true }}
                      className="flex h-full flex-col items-start justify-center gap-y-2 leading-[30px]"
                    />
                  </div>
                  {t.repetition ? (
                    <div className="absolute bottom-[19px] rounded-full bg-[#4a4a4a2f] p-2 text-[15px] text-ramadan-primary ltr:right-[26px] rtl:left-[26px]">
                      {t.repetition}
                    </div>
                  ) : (
                    <div className="absolute bottom-[19px] text-[15px] text-ramadan-primary opacity-70 ltr:right-[26px] rtl:left-[26px]">
                      {t.source}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const RepeatedPrayerListLoading = ({ isLoading }: { isLoading: boolean }) => {
  const skeletonData: { id: string }[] = Array.from({ length: 8 }).map(
    (s, i) => {
      return {
        id: i + "",
      };
    }
  );

  const renderItems = () => {
    return skeletonData.map((s) => {
      return (
        <div key={s.id} className="w-full">
          <Skeleton
            className={`h-[200px] w-full rounded-[12px] bg-[#f4f4f5] dark:bg-[#27272a]`}
          ></Skeleton>
        </div>
      );
    });
  };

  return isLoading ? (
    <div className="mt-[45px] flex h-full w-full flex-col items-start justify-start gap-y-2">
      {renderItems()}
    </div>
  ) : null;
};
